import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, MessageSquare, Phone, Calendar, Mail, FileText } from 'lucide-react'
import { dealsApi, type ActivityType } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface AddDealActivityFormProps {
  dealId: string
  onSuccess?: () => void
  className?: string
}

const ACTIVITY_OPTIONS: { value: ActivityType; label: string; icon: typeof MessageSquare }[] = [
  { value: 'note', label: 'Note', icon: FileText },
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'email_sent', label: 'Email Sent', icon: Mail },
  { value: 'email_received', label: 'Email Received', icon: Mail },
]

export function AddDealActivityForm({
  dealId,
  onSuccess,
  className,
}: AddDealActivityFormProps) {
  const queryClient = useQueryClient()
  const [activityType, setActivityType] = useState<ActivityType>('note')
  const [content, setContent] = useState('')

  const addActivityMutation = useMutation({
    mutationFn: () =>
      dealsApi.addActivity(dealId, {
        activity_type: activityType,
        content: content.trim() || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] })
      setContent('')
      onSuccess?.()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    addActivityMutation.mutate()
  }

  const selectedOption = ACTIVITY_OPTIONS.find((o) => o.value === activityType)
  const Icon = selectedOption?.icon ?? MessageSquare

  return (
    <form onSubmit={handleSubmit} className={cn('space-y-3', className)}>
      <div className="flex gap-2">
        <Select
          value={activityType}
          onValueChange={(v) => setActivityType(v as ActivityType)}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Activity type" />
          </SelectTrigger>
          <SelectContent>
            {ACTIVITY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                <span className="flex items-center gap-2">
                  <opt.icon className="h-4 w-4" />
                  {opt.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Textarea
        placeholder={
          activityType === 'note'
            ? 'Add a note...'
            : activityType === 'call'
            ? 'Call summary...'
            : activityType === 'meeting'
            ? 'Meeting notes...'
            : 'Details...'
        }
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          disabled={addActivityMutation.isPending}
        >
          {addActivityMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Icon className="h-4 w-4 mr-2" />
          )}
          Add {selectedOption?.label ?? 'Activity'}
        </Button>
      </div>
    </form>
  )
}
